import React, { useCallback } from 'react';
import AlertText from 'fe-design-base/molecules/AlertText';
import FDBDatePicker from 'fe-design-base/organisms/FDBDatePicker';
import colors from 'fe-design-base/styles/colors';
import { isEmpty, isNil, partial } from 'lodash';

import NevadaLaborSettings from 'features/employeeProfile/JobDetailsView/AccessWagesWidget/NevadaLaborSettings';

import Box from 'components/Box';
import CheckboxInput from 'components/forms/CheckboxInput';
import RadioGroup from 'components/forms/RadioGroup';
import ToggleInput from 'components/forms/ToggleInput';
import Text from 'components/Text';
import TooltipIcon from 'components/TooltipIcon';

import { formatDate } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import AccessLevel from './AccessLevel/AccessLevel';
import ContactInfo from './ContactInfo/ContactInfo';
import PacketRow from './Onboarding/PacketRow';
import RolesBlock from './RolesBlock';

const RADIO_GROUP_OPTIONS = [
  {
    value: 'packet',
    label: (
      <>
        <Text
          fs16
          navy
          i18n="edit_team.individual_drawer.member_info.request_information"
        />
        &nbsp;
        <TooltipIcon
          content={toI18n(
            'edit_team.individual_drawer.member_info.request_information_tooltip'
          )}
          size="sm"
        />
      </>
    ),
  },
  {
    value: 'manual',
    label: (
      <Text
        fs16
        navy
        i18n="edit_team.individual_drawer.member_info.enter_manually"
      />
    ),
  },
];

const FormPayroll = ({
  user,
  isPayrollUpdating,
  handleChange,
  handleBlur,
  handlePacketChange,
  handleJobTaxClassificationChange,
  jobTaxClassification,
  handleContractorTypeChange,
  contractorType,
  fourTensRuleEnabled,
  setFieldValue,
  isAContractor,
  isEmployee,
  cxEl,
  canViewWages,
  isPayrollSetup,
  hasOnboardingDocsAccess,
  numberOfDocumentsInContractorTemplate,
  numberOfDocumentsInEmployeeTemplate,
  firstName,
  lastName,
  phone,
  email,
  wageAttributes,
  hireDate,
  sendSms,
  packet,
  level,
  includedInPayroll,
  requestInformation,
  errors = {},
  canRequestInformation,
  isEligibleForPayroll,
  userId,
  firstNameMaximum,
  hiringSourceExperiment,
  canEditTaxClassification,
  invalidPayrollInfo,
  hasNoActivePayments,
  hasPaidPayrollRunsForJob,
  hasITIN,
  accessLevelOptions,
}) => {
  const canShowRequiredFieldError = isEligibleForPayroll
    ? false
    : includedInPayroll;

  const handleDateChange = useCallback(
    date => {
      const newDate = formatDate(date.target.value, 'parsable_date');
      handleChange('hireDate', newDate);
    },
    [handleChange]
  );

  const showAlertText =
    canShowRequiredFieldError &&
    isEmpty(hireDate) &&
    jobTaxClassification !== '1099';

  return (
    <Box>
      <Box mb32>
        {canRequestInformation === false && (
          <Box
            pa12
            bg="alertA1"
            className={cxEl('send_packet_warning')}
            bradius={8}
            mb32
          >
            <Text i18n="edit_team.individual_drawer.request_information_packet_warning" />
          </Box>
        )}
        {!isPayrollUpdating ||
          (invalidPayrollInfo(user) && (
            <Box
              pa12
              bg="alertA1"
              className={cxEl('eligibility_warning')}
              bradius={8}
              mb32
            >
              <Text i18n="edit_team.individual_drawer.payroll_eligibility_warning.note" />
              {` ${firstName.substring(0, firstNameMaximum)} `}
              <Text i18n="edit_team.individual_drawer.payroll_eligibility_warning.on_time" />
            </Box>
          ))}
        <ToggleInput
          value={includedInPayroll}
          onChange={partial(handleChange, 'includedInPayroll')}
          label={toI18n('edit_team.individual_drawer.included_in_payroll')}
          uppercase={false}
        />
      </Box>
      <ContactInfo
        handleChange={handleChange}
        handleBlur={handleBlur}
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        email={email}
        requestInformation={requestInformation}
        errors={errors}
        user={user}
      />
      {!isPayrollSetup && (
        <Box mb={24}>
          <CheckboxInput
            name="sendSms"
            value={sendSms}
            label={<Text fs14 i18n="add_team.individual_drawer.send_sms" />}
            onChange={partial(handleChange, 'sendSms')}
          />
        </Box>
      )}
      <Box mb={24} spacebetween>
        <AccessLevel
          cxEl={cxEl}
          handleChange={handleChange}
          level={level}
          accessLevelOptions={accessLevelOptions}
        />
        <Box w={160}>
          <Box mb8>
            <Text fs14 fw600 i18n="add_team.individual_drawer.start_date" />*
          </Box>
          <div
            data-testid={cxEl('start_date')}
            style={{
              outline: showAlertText ? `1px solid ${colors.red500}` : undefined,
              outlineOffset: showAlertText ? '1px' : undefined,
              borderRadius: showAlertText ? '4px' : undefined,
            }}
          >
            <FDBDatePicker
              name="hireDate"
              value={
                hireDate ? formatDate(hireDate, 'parsable_reversed') : null
              }
              onChange={handleDateChange}
              hideClearIcon
              error={errors.hireDate}
            />
          </div>
          {errors.hireDate && (
            <Box absolute mt={4} maxw={160}>
              <AlertText
                variant="error"
                message={
                  errors.hireDate
                    ? errors.hireDate
                    : toI18n(
                        'team.employee_profile.payroll_ee_information.validations.required_field'
                      )
                }
              />
            </Box>
          )}
        </Box>
      </Box>
      {canViewWages && (
        <Box mb={24}>
          <RolesBlock
            wageErrorBorder={
              canShowRequiredFieldError && isNil(wageAttributes[0]?.wage_rate)
            }
            onChange={partial(handleChange, 'wageAttributes')}
            editingRolesData={wageAttributes}
            key={userId}
            isEmployee={isEmployee}
          />
        </Box>
      )}
      {hasOnboardingDocsAccess && (
        <PacketRow
          isPayrollUpdating={isPayrollUpdating}
          hasITIN={hasITIN}
          user={user}
          hasNoActivePayments={hasNoActivePayments}
          hasPaidPayrollRunsForJob={hasPaidPayrollRunsForJob}
          errorBorder={
            canShowRequiredFieldError ||
            (requestInformation === 'packet' && isEmpty(jobTaxClassification))
          }
          handleJobTaxClassificationChange={handleJobTaxClassificationChange}
          jobTaxClassification={jobTaxClassification}
          canEditTaxClassification={canEditTaxClassification}
          handleContractorTypeChange={handleContractorTypeChange}
          contractorType={contractorType}
          selectedPacket={packet}
          handlePacketChange={handlePacketChange}
          numberOfDocumentsInContractorTemplate={
            numberOfDocumentsInContractorTemplate || 0
          }
          numberOfDocumentsInEmployeeTemplate={
            numberOfDocumentsInEmployeeTemplate || 0
          }
          isPayrollSetup={isPayrollSetup}
        />
      )}
      {jobTaxClassification && (
        <NevadaLaborSettings
          cxEl={cxEl}
          setFieldValue={setFieldValue}
          fourTensRuleEnabledValue={fourTensRuleEnabled}
          isAContractor={isAContractor}
          verticalLayout
          editMode
        />
      )}
      <Box
        mt8
        mb={hiringSourceExperiment ? 24 : 64}
        hidden={isPayrollUpdating && !invalidPayrollInfo(user)}
      >
        <Box>
          <Text
            i18n="edit_team.individual_drawer.member_info.label"
            fs16
            navy
          />
        </Box>
        <Box mt16>
          <ul style={{ marginLeft: '16px', listStyleType: 'disc' }}>
            <li>
              <Text
                fs16
                navy
                i18n="edit_team.individual_drawer.member_info.personal_information"
              />
            </li>
            <li>
              <Text
                fs16
                navy
                i18n="edit_team.individual_drawer.member_info.payment_method"
              />
            </li>
            <li>
              <Text
                fs16
                navy
                i18n="edit_team.individual_drawer.member_info.w4"
              />
            </li>
            <li>
              <Text
                fs16
                navy
                i18n="edit_team.individual_drawer.member_info.state_tax"
              />
            </li>
          </ul>
        </Box>
        <Box mt12 ml={-12} className={cxEl('requestInformation')}>
          <RadioGroup
            options={RADIO_GROUP_OPTIONS}
            value={requestInformation}
            onChange={partial(handleChange, 'requestInformation')}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FormPayroll;
